import { useCallback, useEffect, useMemo, useState } from 'react';
import { ConfirmDialog, ArchiveConfirmDialog } from './Modal';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from './Icon';
import { getAccessToken, isSecretaryContextUser } from '../authStorage';
import { fetchMeetings } from '../meetingsApi';
import { archiveEntity } from '../archiveApi';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import './BranchesPage.css';
import './DecisionsPage.css';

const PAGE_SIZE = 10;

function statusBadge(status, t) {
  const v = String(status || 'draft').toLowerCase();
  if (v === 'published') return { text: t('meetings.published'), bg: '#e8f5e9', color: '#2e7d32' };
  return { text: t('common.draft'), bg: '#fff3e0', color: '#ef6c00' };
}
function attendanceBadge(type, t) {
  const v = String(type || 'in_person').toLowerCase();
  if (v === 'virtual') return { text: t('meetings.virtual'), bg: '#e0f2f1', color: '#00796b' };
  return { text: t('meetings.inPerson'), bg: '#e3f2fd', color: '#1565c0' };
}
function initials(name) {
  const s = String(name || '').trim();
  if (!s) return '1';
  const p = s.split(/\s+/);
  return (p[0][0] || '1').toUpperCase();
}

export default function MeetingsPage() {
  const { language, direction } = useLanguage();
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/officer') ? '/officer' : '/app';

  const dateLocale = language === 'ar' || language === 'ur' ? 'ar-SA-u-ca-gregory'
    : language === 'zh' ? 'zh-CN' : language === 'fr' ? 'fr-FR' : 'en-US';

  const [accessDenied, setAccessDenied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [archiveConfirmId, setArchiveConfirmId] = useState(null);
  const [archiving, setArchiving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const meetings = await fetchMeetings();
      setRows(meetings);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('meetings.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (!getAccessToken()) { navigate('/login', { replace: true }); return; }
    if (!isSecretaryContextUser()) { setAccessDenied(true); setLoading(false); return; }
    loadData();
  }, [loadData, navigate]);

  const filteredRows = useMemo(() => rows.filter((x) => {
    const q = query.trim().toLowerCase();
    const st = String(x.status || 'draft').toLowerCase();
    const statusText = st === 'published' ? 'منشور published' : 'مسودة draft';
    const matchesQ = !q || String(x.title || '').toLowerCase().includes(q) || statusText.includes(q);
    const matchesStatus = statusFilter === 'all' || st === statusFilter;
    return matchesQ && matchesStatus;
  }), [rows, query, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const paged = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (accessDenied) return <p style={{ padding: 24, color: '#78909c', fontFamily: 'Cairo, sans-serif' }}>{t('common.noPerm')}</p>;

  return (
    <div className="page" dir={direction}>
      <div className="list-page-card list-page-card--unified">
        <header className="list-page-card__header">
          <div className="list-page-card__titles">
            <span className="list-page-card__title"><Icon name="calendar" size={18} color="#00334a" style={{ verticalAlign: 'middle', marginLeft: 6 }} />{t('meetings.title')}</span>
            <span className="list-page-card__sub">{t('meetings.subtitle')}</span>
          </div>
          <div className="list-page-card__actions">
            <button type="button" className="btn btn--ghost" onClick={() => navigate(`${basePath}/archive?type=Meeting`)}><Icon name="archive" size={14} /> {t('nav.archive')}</button>
            <button type="button" className="btn btn--primary" onClick={() => navigate(`${basePath}/meetings/create`)}>{t('meetings.create')}</button>
          </div>
        </header>

        {error && <div className="list-page-card__banner list-page-card__banner--error">{error}</div>}
        {loading && <div className="list-page-card__loading">{t('common.loading')}</div>}

        {!loading && (
        <>
          <div className="page__filters">
            <select className="page__filter-select" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="all">{t('meetings.allStatuses')}</option>
              <option value="draft">{t('common.draft')}</option>
              <option value="published">{t('meetings.published')}</option>
            </select>
            <div className="page__search">
              <Icon name="search" size={14} color="#90a4ae" className="page__search-icon" />
              <input className="page__search-input" placeholder={t('meetings.searchByStatus')} value={query} onChange={(e) => { setQuery(e.target.value); setPage(1); }} />
            </div>
          </div>

          <div className="table-card">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 42 }}>#</th>
                  <th>{t('meetings.colMeeting')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('common.date')}</th>
                  <th>{t('meetings.colTime')}</th>
                  <th>{t('meetings.colAttendance')}</th>
                  <th>{t('meetings.colMembers')}</th>
                  <th>{t('common.view')}</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((row, idx) => {
                  const start = row.startAtUtc ? new Date(row.startAtUtc) : null;
                  const sb = statusBadge(row.status, t);
                  const ab = attendanceBadge(row.attendanceType, t);
                  const invitees = Array.isArray(row.invitees) ? row.invitees : [];
                  return (
                    <tr key={row.id}>
                      <td className="td-num">{(page - 1) * PAGE_SIZE + idx + 1}</td>
                      <td><strong>{row.title}</strong></td>
                      <td><span style={{ padding: '4px 8px', borderRadius: 999, background: sb.bg, color: sb.color, fontWeight: 700, fontSize: '.76rem' }}>{sb.text}</span></td>
                      <td>{start ? start.toLocaleDateString(dateLocale) : '—'}</td>
                      <td dir="ltr">{start ? start.toLocaleTimeString(dateLocale, { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                      <td><span style={{ padding: '4px 8px', borderRadius: 999, background: ab.bg, color: ab.color, fontWeight: 700, fontSize: '.76rem' }}>{ab.text}</span></td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          {invitees.slice(0, 4).map((m, i) => (
                            <span key={`${m.targetType}:${m.targetId}:${i}`} style={{ width: 24, height: 24, borderRadius: 999, background: '#ede7f6', color: '#6a1b9a', display: 'grid', placeItems: 'center', fontSize: '.72rem', fontWeight: 800 }}>
                              {initials(m.displayName)}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>
                        <div className="td-actions">
                          <button type="button" className="btn btn--ghost" onClick={() => navigate(`${basePath}/meetings/${row.id}`)}>{t('common.view')}</button>
                          <button type="button" className="action-btn action-btn--archive" title={t('archive.title')} onClick={() => setArchiveConfirmId(row.id)}><Icon name="archive" size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {paged.length === 0 && <div className="users-empty">{t('meetings.noMeetings')}</div>}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px' }}>
              <div style={{ color: '#78909c', fontSize: '.82rem' }}>{t('meetings.rowsPerPage')}: 10</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="pagination__btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button key={p} className={`pagination__btn${p === page ? ' pagination__btn--active' : ''}`} onClick={() => setPage(p)}>{p}</button>
                ))}
                <button className="pagination__btn" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>›</button>
              </div>
            </div>
          </div>
        </>
        )}
      </div>

      <ArchiveConfirmDialog
        open={!!archiveConfirmId}
        onClose={() => setArchiveConfirmId(null)}
        onConfirm={async () => {
          if (!archiveConfirmId) return;
          setArchiving(true);
          try {
            await archiveEntity('Meeting', archiveConfirmId);
            setRows((p) => p.filter((r) => r.id !== archiveConfirmId));
          } catch { /* ignore */ }
          setArchiving(false);
          setArchiveConfirmId(null);
        }}
        itemName={rows.find((r) => r.id === archiveConfirmId)?.title}
        title={t('archive.title')}
        confirmLabel={t('archive.title')}
        cancelLabel={t('common.cancel')}
        loading={archiving}
      />
    </div>
  );
}
